import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import AvatarImage from 'components/avatar-image-view';
import AvatarEditorContainer from '../containers/avatar-editor-container';
import {defineMessages, injectIntl} from 'react-intl';
import classes from './personal-info.scss';
import CloseButton from 'components/close-button-view';
import st from 'core/shared-translations';
import { getModalParent } from 'lib/utility-functions';

if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#root');
}

const t = defineMessages({
  'avatar-selector.change-profile-photo': {
    id: 'avatar-selector.change-profile-photo',
    defaultMessage: 'Change your profile photo',
    description: 'Modal dialog title for changing the profile photo',
  },
  'avatar-selector.drag-drop-picture-here': {
    id: 'avatar-selector.drag-drop-picture-here',
    defaultMessage: 'Drop photo here',
    description: 'Drag and drop zone instruction line 1',
  },
  'avatar-selector.drag-drop-select-an-image': {
    id: 'avatar-selector.drag-drop-select-an-image',
    defaultMessage: 'choose a photo',
    description: 'Drag and drop zone instruction line 2 phrase: select an image',
  },
  'avatar-selector.drag-drop-image-formats-accepted': {
    id: 'avatar-selector.drag-drop-image-formats-accepted',
    defaultMessage: 'For best results, use a .png or .jpg file smaller than 2 MB.',
    description: 'Drag and drop zone instruction line 3 - image formats accepted',
  },
  'avatar-selector.reset-to-default-photo': {
    id: 'avatar-selector.reset-to-default-photo',
    defaultMessage: 'Reset to default photo',
    description: 'Modal dialog action link to reset user\'s default photo',
  },
  'avatar-selector.avatar-titletext-custom': {
    id: 'avatar-selector.avatar-titletext-custom',
    defaultMessage: 'Custom avatar image',
    description: 'Title (hover) text to display on mouseover of avatar image, when a custom image is loaded',
  },
  'avatar-selector.avatar-titletext-default': {
    id: 'avatar-selector.avatar-titletext-default',
    defaultMessage: 'Default avatar image',
    description: 'Title (hover) text to display on mouseover of avatar image, when a generic image is loaded',
  },
});

class AvatarSelector extends Component {
  render() {
    const {formatMessage} = this.props.intl;
    return (
      <div id="avatar-selection-view">
        <AvatarImage
          avatarImageUrl={this.props.avatarImageUrl}
          changeProfilePictureText={formatMessage(t['avatar-selector.change-profile-photo'])}
          onAvatarSelectLinkClick={this.props.onAvatarSelectLinkClick}
          isViewOnly={this.props.isViewOnly}
          titleText={
            formatMessage(t[this.props.hasDefaultAvatarImage ?
              'avatar-selector.avatar-titletext-default' :
              'avatar-selector.avatar-titletext-custom'])
          }
        />

        <Modal
          isOpen={this.props.modalIsOpen}
          onRequestClose={this.props.closeModal}
          contentLabel="Modal"
          shouldCloseOnOverlayClick={false}
          className={classes['modal']}
          overlayClassName={classes['overlay']}
          parentSelector={getModalParent}
        >
          <div style={{textAlign: 'right'}}>
            <CloseButton
              ariaLabel={formatMessage(st['shared.close'])}
              onClick={this.props.closeModal}
            />
          </div>
          <div className={classes['modalTitle']}>
            {formatMessage(t['avatar-selector.change-profile-photo'])}
          </div>
          <form name="ImageUploadForm" style={{textAlign: 'center'}}>
            <div id="ImageUploadSelect" className={`${this.props.imageUploadSelectClass}`}>
              <div
                className={`dropBox${this.props.dropBoxClass}`}
                id="DropBox"
                onDragOver={this.props.fileDragHover}
                onDragLeave={this.props.fileDragHover}
                onDrop={this.props.fileSelectHandler}
                onChange={this.props.fileSelectHandler}
              >
                <i id="cloud" className={`togo-icon togo-icon-picture ${classes['dropBoxImage']}`} />
                <div className={classes['dropBoxLine1']}>
                  {formatMessage(t['avatar-selector.drag-drop-picture-here'])}
                </div>
                <div className={classes['dropBoxLine2']}>
                  <input
                    type="file"
                    id="fileSelector"
                    name="fileSelector"
                    className="hidden"
                    onChange={this.props.fileSelectHandler}
                  />
                  <span>{`${formatMessage(st['shared.or'])} `}</span>
                  <a href="#" id="choosePhotoLink" onClick={this.props.fileSelectorClick}>
                    {formatMessage(t['avatar-selector.drag-drop-select-an-image'])}
                  </a>
                </div>
                <div className={classes['dropBoxLine3']}>
                  {formatMessage(t['avatar-selector.drag-drop-image-formats-accepted'])}
                </div>
              </div>
              <div className={classes['modalResetPhotoLink']}>
                {!this.props.hasDefaultAvatarImage &&
                  <a href="#" id="resetToDefaultPhoto" onClick={this.props.resetToDefaultAvatar}>
                    {formatMessage(t['avatar-selector.reset-to-default-photo'])}
                  </a>}
              </div>
            </div>
            <div
              id="ImageEditAndSubmit"
              className={`${this.props.imageEditAndSubmitClass}`}
            >
              <AvatarEditorContainer
                userImageUrl={this.props.userImageUrl}
                avatarUpdateUrl={this.props.avatarUpdateUrl}
                closeModal={this.props.closeModal}
                processImageAndUpload={this.props.processImageAndUpload}
                showImageSelectorControl={this.props.showImageSelectorControl}
              />
            </div>
          </form>
        </Modal>
      </div>
    );
  }
}

AvatarSelector.propTypes = {
  avatarImageUrl: PropTypes.string.isRequired,
  avatarUpdateUrl: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  dropBoxClass: PropTypes.string.isRequired,
  fileDragHover: PropTypes.func.isRequired,
  fileSelectHandler: PropTypes.func.isRequired,
  fileSelectorClick: PropTypes.func.isRequired,
  hasDefaultAvatarImage: PropTypes.bool.isRequired,
  imageEditAndSubmitClass: PropTypes.string.isRequired,
  imageUploadSelectClass: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  modalIsOpen: PropTypes.bool.isRequired,
  onAvatarSelectLinkClick: PropTypes.func.isRequired,
  processImageAndUpload: PropTypes.func.isRequired,
  resetToDefaultAvatar: PropTypes.func.isRequired,
  showImageSelectorControl: PropTypes.func.isRequired,
  userImageUrl: PropTypes.string,
  isViewOnly: PropTypes.bool,
};

export default injectIntl(AvatarSelector);
